import { useEffect, useMemo } from "react";
import { useAccount } from "@/state/server";
import { useScrollParent } from "@/hooks/useScrollParent";
import { type DocumentType } from "@/utils";
import { LibraryRoot } from "@/components/template-library/LibraryRoot";
import { LibraryDocTypeView } from "@/components/template-library/LibraryDocTypeView";
import { getTemplateGroupByPath, groupKeyToTitle } from "./utils";
import { useTemplateLibrary } from "./data";
import { LibrarySubGroupView } from "./LibrarySubGroupView";

export type TemplateLibraryProps = {
  groupPath: string[];
  onGroupPathChange: (groupPath: string[]) => unknown;
  onCloneSuccess: (docType: DocumentType) => unknown;
};

export function TemplateLibrary({
  groupPath,
  onGroupPathChange,
  onCloneSuccess,
}: TemplateLibraryProps) {
  const account = useAccount();
  const scrollParent = useScrollParent();
  const templateLibrary = useTemplateLibrary();

  const currentGroupName = groupPath?.at(-1);
  const currentGroup = useMemo(
    () =>
      !!groupPath?.length &&
      templateLibrary &&
      getTemplateGroupByPath(templateLibrary.library, groupPath),
    [groupPath, templateLibrary]
  );

  const currentGroupIsDocType = currentGroup && !("children" in currentGroup);
  const currentGroupIsSubGroup =
    currentGroupName && currentGroup && "children" in currentGroup;

  useEffect(() => {
    scrollParent.scrollTo({ top: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupPath]);

  return (
    <div ref={scrollParent.baseElementRef} data-testid="template-library">
      {!currentGroupIsDocType && !currentGroupIsSubGroup && (
        <div className="my-8 " data-testid="template-library-root-view">
          <div className="animate-fade-in">
            <LibraryRoot
              baseUrl={templateLibrary?.baseUrl}
              libraryGroups={templateLibrary?.library}
              pinnedGroupKey={
                account?.industry
                  ? groupKeyToTitle(account.industry)
                  : undefined
              }
              onGroupPathChange={onGroupPathChange}
            />
          </div>
        </div>
      )}
      {!currentGroupIsDocType &&
        currentGroupIsSubGroup &&
        templateLibrary?.baseUrl && (
          <div
            key={currentGroupName}
            data-testid="template-library-subgroup-view"
          >
            <h1 className="text-2xl sm:text-4xl font-semibold mb-ds-3 sm:mb-ds-6">
              {currentGroupName}
            </h1>
            <div className="my-4 sm:my-8 font-semibold">
              <div className="animate-fade-in">
                <LibrarySubGroupView
                  baseUrl={templateLibrary.baseUrl}
                  librarySubGroup={currentGroup}
                  onGroupClick={(subgroup) =>
                    onGroupPathChange([...groupPath, subgroup])
                  }
                />
              </div>
            </div>
          </div>
        )}
      {currentGroupIsDocType && (
        <div
          className="animate-fade-in"
          data-testid="template-library-doctype-view"
        >
          <LibraryDocTypeView
            libraryPath={groupPath}
            libraryDocType={currentGroup}
            onCloneSuccess={onCloneSuccess}
          />
        </div>
      )}
    </div>
  );
}
